import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ImagePlus, X } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { GALLERY, GALLERY_CATEGORIES, PLACEHOLDER_SLOTS } from "@/lib/site";
import usePageTitle from "@/lib/usePageTitle";

export default function Work() {
  usePageTitle("Our Work — Real Custom Projects | Skies The Limit Creations LLC");
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  return (
    <div className="grain">
      <header className="relative overflow-hidden pb-10 pt-[130px] lg:pt-[160px]">
        <div aria-hidden className="pointer-events-none absolute -top-32 left-[-10%] h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-testid="work-header">
          <Reveal>
            <p className="eyebrow mb-5">Our work</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Real projects, really made.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist sm:text-lg">
              Every photo below is work we completed — no stock images. The gallery grows as we photograph new pieces,
              organized by the service that made them.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-3" role="tablist" aria-label="Filter gallery by service">
              {GALLERY_CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={filter === c}
                  onClick={() => setFilter(c)}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}-button`}
                  className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                    filter === c
                      ? "border-gold/60 bg-gold/10 text-gold"
                      : "border-white/15 text-mist hover:border-white/40 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="work-gallery-grid">
          {items.map((g, i) => (
            <Reveal key={g.id} delay={(i % 3) * 0.08}>
              <button
                type="button"
                onClick={() => setActive(g)}
                data-testid={`work-item-${g.id}`}
                className="group block w-full text-left"
                aria-label={`View larger: ${g.caption}`}
              >
                <div className="overflow-hidden rounded-2xl border border-white/10">
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 px-1">
                  <span className="text-sm text-white/85">{g.caption}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold">{g.category}</span>
                </div>
              </button>
            </Reveal>
          ))}

          {PLACEHOLDER_SLOTS.filter((p) => filter === "All").map((p) => (
            <div key={p.id} className="placeholder-frame min-h-[300px]" data-testid={`work-placeholder-${p.id}`}>
              <ImagePlus className="h-8 w-8 text-gold/60" aria-hidden />
              <p className="font-display text-base font-bold text-white">{p.title}</p>
              <p className="max-w-[240px] text-xs leading-relaxed text-mist">{p.note}</p>
              <p className="text-[11px] text-mist/60">This slot is ready for new photos</p>
            </div>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 rounded-2xl border border-dashed border-white/15 bg-panel/50 px-6 py-8 text-center" data-testid="work-owner-note">
            <p className="text-sm text-mist">
              <span className="font-semibold text-gold">Draft note:</span> every photo above is real work from the
              shop — including the newest laser engraving and 3D printing pieces. Send new photos any time; the empty
              slots show where the next ones go.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-6 flex flex-col items-center gap-4 text-center">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">Have something like this in mind?</h2>
            <Link to="/quote" data-testid="work-quote-cta-button" className="btn-primary">
              Request a Quote <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </Reveal>
      </main>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-5 backdrop-blur-md"
            onClick={() => setActive(null)}
            data-testid="work-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={active.caption}
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              aria-label="Close image view"
              data-testid="work-lightbox-close-button"
              className="absolute right-5 top-5 rounded-full border border-white/20 p-2.5 text-white transition-colors duration-200 hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={active.alt} className="max-h-[76vh] w-auto rounded-2xl border border-white/10 object-contain" />
              <figcaption className="mt-4 flex items-center justify-between gap-4">
                <span className="text-sm text-white/85">{active.caption}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold">{active.category}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
